import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon } from 'lucide-react';
type FormData = {
  revenue: string;
  timeInBusiness: string;
  creditScore: string;
  name: string;
  email: string;
  phone: string;
};
export function PreQualForm() {
  const [currentStep, setCurrentStep] = useState(1);
  const [smsConsent, setSmsConsent] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    revenue: '',
    timeInBusiness: '',
    creditScore: '',
    name: '',
    email: '',
    phone: ''
  });
  const revenueOptions = ['Under $10K', '$10K–$25K', '$25K–$50K', '$50K–$100K', '$100K+'];
  const timeOptions = ['Less than 6 months', '6–12 months', '1–2 years', '2–5 years', '5+ years'];
  const creditOptions = ['Poor (Below 550)', 'Fair (550–649)', 'Good (650–719)', 'Excellent (720+)'];
  const handleOptionSelect = (field: keyof FormData, value: string) => {
    setFormData({
      ...formData,
      [field]: value
    });
    setTimeout(() => setCurrentStep(currentStep + 1), 300);
  };
  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await fetch('https://hooks.zapier.com/hooks/catch/27089849/u7yc0z8/', {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          revenue: formData.revenue,
          timeInBusiness: formData.timeInBusiness,
          creditScore: formData.creditScore,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          smsConsent: smsConsent,
          source: 'MagicPatterns Funnel'
        })
      });
      console.log('Form submitted:', formData);
      alert("Thank you! We'll be in touch shortly with your funding options.");
      setFormData({
        revenue: '',
        timeInBusiness: '',
        creditScore: '',
        name: '',
        email: '',
        phone: ''
      });
      setSmsConsent(false);
      setCurrentStep(1);
    } catch (error) {
      console.error('Submission error:', error);
      alert('Something went wrong. Please try again.');
    }
  };
  const canSubmit = formData.name && formData.email && formData.phone && smsConsent;
  return <section id="qualify-form" className="py-12 px-4 bg-charcoal" data-id="element-80">
      <div className="max-w-3xl mx-auto" data-id="element-81">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-10" data-id="element-82">
          <h2 className="font-serif text-4xl md:text-5xl mb-3" data-id="element-83">
            See If You Qualify in 60 Seconds
          </h2>
          <p className="text-gold-primary font-medium mb-1" data-id="element-84">
            Fast approvals. Real funding solutions.
          </p>
          <p className="text-gray-400 text-sm font-medium mb-6" data-id="element-85">
            No impact on your credit to check
          </p>
          <div className="flex justify-center gap-2 mt-4" data-id="element-86">
            {[1, 2, 3, 4].map(step => <div key={step} className={`h-2 w-16 rounded-full transition-all duration-300 ${step <= currentStep ? 'bg-gold-primary' : 'bg-gray-700'}`} data-id="element-87" />)}
          </div>
        </motion.div>

        <div className="bg-black border border-gray-800 rounded-2xl p-6 md:p-8" data-id="element-88">
          <AnimatePresence mode="wait" data-id="element-89">
            {currentStep === 1 && <motion.div key="step1" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} data-id="element-90">
                <h3 className="text-2xl font-bold mb-6" data-id="element-91">
                  What's your monthly revenue?
                </h3>
                <div className="space-y-3" data-id="element-92">
                  {revenueOptions.map(option => <button key={option} onClick={() => handleOptionSelect('revenue', option)} className={`w-full p-4 rounded-lg border-2 text-left transition-all duration-200 hover:border-gold-primary hover:bg-charcoal ${formData.revenue === option ? 'border-gold-primary bg-charcoal' : 'border-gray-700 bg-black'}`} data-id="element-93">
                      {option}
                    </button>)}
                </div>
              </motion.div>}

            {currentStep === 2 && <motion.div key="step2" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} data-id="element-94">
                <button onClick={() => setCurrentStep(1)} className="flex items-center gap-2 text-gold-primary mb-6 hover:text-gold-accent transition-colors" data-id="element-95">
                  <ChevronLeftIcon className="w-5 h-5" data-id="element-96" />
                  Back
                </button>
                <h3 className="text-2xl font-bold mb-6" data-id="element-97">
                  How long have you been in business?
                </h3>
                <div className="space-y-3" data-id="element-98">
                  {timeOptions.map(option => <button key={option} onClick={() => handleOptionSelect('timeInBusiness', option)} className={`w-full p-4 rounded-lg border-2 text-left transition-all duration-200 hover:border-gold-primary hover:bg-charcoal ${formData.timeInBusiness === option ? 'border-gold-primary bg-charcoal' : 'border-gray-700 bg-black'}`} data-id="element-99">
                      {option}
                    </button>)}
                </div>
              </motion.div>}

            {currentStep === 3 && <motion.div key="step3" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} data-id="element-100">
                <button onClick={() => setCurrentStep(2)} className="flex items-center gap-2 text-gold-primary mb-6 hover:text-gold-accent transition-colors" data-id="element-101">
                  <ChevronLeftIcon className="w-5 h-5" data-id="element-102" />
                  Back
                </button>
                <h3 className="text-2xl font-bold mb-6" data-id="element-103">
                  What's your credit score range?
                </h3>
                <div className="space-y-3" data-id="element-104">
                  {creditOptions.map(option => <button key={option} onClick={() => handleOptionSelect('creditScore', option)} className={`w-full p-4 rounded-lg border-2 text-left transition-all duration-200 hover:border-gold-primary hover:bg-charcoal ${formData.creditScore === option ? 'border-gold-primary bg-charcoal' : 'border-gray-700 bg-black'}`} data-id="element-105">
                      {option}
                    </button>)}
                </div>
              </motion.div>}

            {currentStep === 4 && <motion.div key="step4" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} data-id="element-106">
                <button onClick={() => setCurrentStep(3)} className="flex items-center gap-2 text-gold-primary mb-6 hover:text-gold-accent transition-colors" data-id="element-107">
                  <ChevronLeftIcon className="w-5 h-5" data-id="element-108" />
                  Back
                </button>
                <h3 className="text-2xl font-bold mb-6" data-id="element-109">
                  Almost there! Enter your contact info
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4" data-id="element-110">
                  <div data-id="element-111">
                    <label htmlFor="name" className="block text-sm font-medium mb-2" data-id="element-112">
                      Full Name
                    </label>
                    <input type="text" id="name" value={formData.name} onChange={e => handleInputChange('name', e.target.value)} className="w-full p-4 rounded-lg bg-charcoal border-2 border-gray-700 focus:border-gold-primary focus:outline-none transition-colors" required data-id="element-113" />
                  </div>
                  <div data-id="element-114">
                    <label htmlFor="email" className="block text-sm font-medium mb-2" data-id="element-115">
                      Email Address
                    </label>
                    <input type="email" id="email" value={formData.email} onChange={e => handleInputChange('email', e.target.value)} className="w-full p-4 rounded-lg bg-charcoal border-2 border-gray-700 focus:border-gold-primary focus:outline-none transition-colors" required data-id="element-116" />
                  </div>
                  <div data-id="element-117">
                    <label htmlFor="phone" className="block text-sm font-medium mb-2" data-id="element-118">
                      Phone Number
                    </label>
                    <input type="tel" id="phone" value={formData.phone} onChange={e => handleInputChange('phone', e.target.value)} className="w-full p-4 rounded-lg bg-charcoal border-2 border-gray-700 focus:border-gold-primary focus:outline-none transition-colors" required data-id="element-119" />
                  </div>
                  <div className="flex items-start gap-3 mt-2" data-id="element-120">
                    <input type="checkbox" id="smsConsent" checked={smsConsent} onChange={e => setSmsConsent(e.target.checked)} className="mt-1 h-4 w-4 shrink-0 rounded border-gray-600 bg-charcoal accent-gold-primary cursor-pointer" required data-id="element-121" />
                    <label htmlFor="smsConsent" className="text-xs text-gray-400 leading-relaxed cursor-pointer select-none" data-id="element-122">
                      I agree to receive calls and text messages from
                      Cotifunding regarding my funding request. Message and data
                      rates may apply. Reply STOP to opt out.
                    </label>
                  </div>
                  <motion.button type="submit" disabled={!canSubmit} animate={canSubmit ? {
                boxShadow: ['0 0 20px rgba(240,185,11,0.3)', '0 0 40px rgba(240,185,11,0.6)', '0 0 20px rgba(240,185,11,0.3)']
              } : {}} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="w-full bg-gold-primary text-black font-bold text-xl px-8 py-5 rounded-lg hover:bg-gold-accent transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed mt-6" data-id="element-123">
                    Get My Options
                  </motion.button>
                  <p className="text-center text-xs text-gray-500 mt-3 flex items-center justify-center gap-1" data-id="element-124">
                    Your info is 100% secure. No obligation. By submitting this
                    form, you agree to receive calls and text messages from
                    Cotifunding.
                  </p>
                </form>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </section>;
}